import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import {
  Loader2, Target, AlertTriangle, Info, XCircle, Calculator,
  RotateCcw, Sliders, ChevronDown, CheckCircle2
} from 'lucide-react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { toast } from 'sonner';

const PARAM_KEYS = [
  'janela_meses', 'pro_labore_fixo', 'verba_pontuais', 'fator_seguranca',
  'reserva_meses', 'reserva_valor_fixo', 'prazo_recompor_meses', 'prazo_estresse_meses'
];

const avisoStyle = (tipo) => {
  if (tipo === 'perigo') return { bg: 'bg-red-500/10', border: 'border-red-500/40', text: 'text-red-300', Icon: XCircle };
  if (tipo === 'alerta') return { bg: 'bg-amber-500/10', border: 'border-amber-500/40', text: 'text-amber-300', Icon: AlertTriangle };
  return { bg: 'bg-blue-500/10', border: 'border-blue-500/40', text: 'text-blue-300', Icon: Info };
};

// Linha da "conta aberta" do cálculo
const Linha = ({ label, valor, op, bold, destaque }) => (
  <div className={`flex items-center justify-between py-2 ${destaque ? 'border-t border-zinc-700 mt-1 pt-3' : ''}`}>
    <span className={`text-sm ${bold ? 'text-white font-medium' : 'text-zinc-400'}`}>
      {op && <span className="text-zinc-500 mr-1">{op}</span>}{label}
    </span>
    <span className={`font-numbers text-sm ${bold ? 'text-white font-semibold' : 'text-zinc-300'}`}>
      {typeof valor === 'string' ? valor : formatCurrency(valor)}
    </span>
  </div>
);

export const CapacidadeInvestimentoModal = ({ open, onOpenChange, gymId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [paramsOpen, setParamsOpen] = useState(false);

  // Simulador de parcela
  const [sim, setSim] = useState({ valor: '', prazo: '12', taxa: '1.5' });

  const applyDraftFromData = (d) => {
    if (!d?.params) return;
    const next = {};
    PARAM_KEYS.forEach((k) => {
      const p = d.params[k];
      next[k] = p && p.editado ? p.valor : null;
    });
    setDraft(next);
  };

  const load = useCallback(async () => {
    if (!gymId) return;
    try {
      setLoading(true);
      const res = await api.get(`/capacidade-investimento?gym_id=${gymId}`);
      setData(res.data);
      applyDraftFromData(res.data);
    } catch (e) {
      toast.error('Erro ao carregar a capacidade de investimento');
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const salvarParams = async () => {
    if (!gymId) return;
    try {
      setSaving(true);
      const res = await api.put(`/capacidade-investimento/params?gym_id=${gymId}`, draft);
      setData(res.data);
      applyDraftFromData(res.data);
      toast.success('Parâmetros atualizados');
    } catch (e) {
      toast.error('Erro ao salvar os parâmetros');
    } finally {
      setSaving(false);
    }
  };

  const voltarTudoPadrao = async () => {
    const reset = {};
    PARAM_KEYS.forEach((k) => { reset[k] = null; });
    if (!gymId) return;
    try {
      setSaving(true);
      const res = await api.put(`/capacidade-investimento/params?gym_id=${gymId}`, reset);
      setData(res.data);
      applyDraftFromData(res.data);
      toast.success('Parâmetros restaurados ao padrão');
    } catch (e) {
      toast.error('Erro ao restaurar');
    } finally {
      setSaving(false);
    }
  };

  const p = data?.params || {};
  const calc = data?.calculo || {};
  const editado = (k) => draft[k] != null;
  const efetivo = (k) => (draft[k] != null ? draft[k] : (p[k]?.padrao ?? ''));
  const setD = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  // ===== Simulador PMT (client-side) =====
  const simValor = parseFloat(sim.valor) || 0;
  const simPrazo = parseInt(sim.prazo) || 0;
  const simTaxa = (parseFloat(sim.taxa) || 0) / 100;
  let pmt = 0;
  if (simValor > 0 && simPrazo > 0) {
    pmt = simTaxa > 0
      ? simValor * (simTaxa * Math.pow(1 + simTaxa, simPrazo)) / (Math.pow(1 + simTaxa, simPrazo) - 1)
      : simValor / simPrazo;
  }
  const totalSim = pmt * simPrazo;
  const jurosSim = totalSim - simValor;
  const capMensal = calc.capacidade_mensal || 0;
  const margem = capMensal - pmt;
  let veredito = null;
  if (pmt > 0) {
    if (pmt <= capMensal) veredito = 'cabe';
    else veredito = 'nao';
  }

  const ParamRow = ({ k, label, suffix, isPercent, step, min, max, help }) => {
    const val = isPercent ? (efetivo(k) === '' ? '' : Math.round(efetivo(k) * 100)) : efetivo(k);
    return (
      <div className="flex flex-col gap-1 py-2 border-b border-zinc-800 last:border-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-zinc-300 flex items-center gap-1.5">
            {label}
            {editado(k) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">editado</span>}
          </span>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              step={step || 'any'}
              min={min}
              max={max}
              value={val}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') { setD(k, null); return; }
                const num = parseFloat(raw);
                setD(k, isPercent ? num / 100 : num);
              }}
              className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-white font-numbers text-right"
              data-testid={`cap-param-${k}`}
            />
            {suffix && <span className="text-xs text-zinc-500 w-8">{suffix}</span>}
            <button
              onClick={() => setD(k, null)}
              title="Voltar ao padrão"
              className="text-zinc-500 hover:text-white p-1"
              data-testid={`cap-param-reset-${k}`}
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {help && <span className="text-[11px] text-zinc-500">{help}</span>}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="capacidade-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-400" />
            Capacidade mensal de investimento
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Calculando...
          </div>
        ) : !data ? null : !data.suficiente ? (
          <div className="py-10 text-center" data-testid="cap-insuficiente">
            <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <p className="text-zinc-200">{data.mensagem_insuficiente}</p>
            <p className="text-xs text-zinc-500 mt-2">Meses fechados disponíveis: {data.meses_historico_disponiveis}</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Card principal */}
            <div className="p-5 rounded-xl bg-gradient-to-br from-blue-500/15 to-emerald-500/10 border border-blue-500/30 text-center">
              <p className="text-xs text-zinc-400 uppercase tracking-wide">Você pode comprometer até</p>
              <p className="text-4xl font-bold text-white font-numbers my-1" data-testid="cap-valor-principal">
                {formatCurrency(calc.capacidade_mensal)}
              </p>
              <p className="text-xs text-zinc-400">por mês, com segurança</p>
              {data.janela_usada?.length > 0 && (
                <p className="text-[11px] text-zinc-500 mt-2">
                  Base: {data.meses_historico_disponiveis} {data.meses_historico_disponiveis === 1 ? 'mês fechado' : 'meses fechados'} ({data.janela_usada.join(', ')})
                </p>
              )}
            </div>

            {/* Avisos */}
            {data.avisos?.map((a, i) => {
              const s = avisoStyle(a.tipo);
              return (
                <div key={i} className={`flex items-start gap-2 p-3 rounded-lg border ${s.bg} ${s.border}`} data-testid={`cap-aviso-${i}`}>
                  <s.Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.text}`} />
                  <p className={`text-xs ${s.text}`}>{a.texto}</p>
                </div>
              );
            })}

            {/* Conta aberta */}
            <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50" data-testid="cap-conta-aberta">
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Como chegamos a esse número</p>
              <Linha label="Geração base (operação)" valor={calc.geracao_base} />
              <Linha label="Parcelas do próximo mês" valor={-calc.parcelas_proximo_mes} op="(−)" />
              <Linha label="Pró-labore fixo" valor={-calc.pro_labore} op="(−)" />
              <Linha label="Verba de pontuais" valor={-calc.pontuais} op="(−)" />
              <Linha label="Sobra livre" valor={calc.sobra_livre} bold destaque />
              <Linha label={`Fator de segurança (${Math.round((calc.fator_seguranca || 0) * 100)}%)`} valor={`× ${Math.round((calc.fator_seguranca || 0) * 100)}%`} />
              <Linha label="Capacidade bruta" valor={calc.capacidade_bruta} bold />
              <Linha label="Recompor reserva" valor={-calc.ajuste_reserva} op="(−)" />
              {calc.estresse_reduziu ? (
                <>
                  <Linha label="Capacidade antes do estresse" valor={calc.capacidade_antes_estresse} bold />
                  <Linha label="Ajuste do teste de estresse" valor={-(calc.capacidade_antes_estresse - calc.capacidade_mensal)} op="(−)" />
                  <Linha label="Capacidade mensal" valor={calc.capacidade_mensal} bold destaque />
                </>
              ) : (
                <Linha label="Capacidade mensal" valor={calc.capacidade_mensal} bold destaque />
              )}
              <p className="text-[11px] text-zinc-500 mt-2">
                Reserva mínima: {formatCurrency(calc.reserva_minima)} · Caixa atual: {formatCurrency(data.caixa_atual)} · Custo operacional médio: {formatCurrency(calc.custo_op_medio)}
              </p>
            </div>

            {/* Parâmetros editáveis */}
            <div className="rounded-lg border border-zinc-700/50 bg-zinc-800/30">
              <button
                onClick={() => setParamsOpen((v) => !v)}
                className="w-full flex items-center justify-between p-3 text-left"
                data-testid="cap-toggle-params"
              >
                <span className="text-sm text-white flex items-center gap-2">
                  <Sliders className="w-4 h-4 text-blue-400" /> Ajustar parâmetros
                </span>
                <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${paramsOpen ? 'rotate-180' : ''}`} />
              </button>
              {paramsOpen && (
                <div className="px-3 pb-3">
                  <ParamRow k="janela_meses" label="Janela de histórico" suffix="meses" step="1" min="3" max="12" help="3 a 12 meses" />
                  <ParamRow k="pro_labore_fixo" label="Pró-labore fixo mensal" step="0.01" min="0" help={`Padrão: ${formatCurrency(p.pro_labore_fixo?.padrao || 0)} (mediana das retiradas)`} />
                  <ParamRow k="verba_pontuais" label="Verba mensal de pontuais" step="0.01" min="0" help={`Padrão: ${formatCurrency(p.verba_pontuais?.padrao || 0)} (mediana dos pontuais/investimentos)`} />
                  <ParamRow k="fator_seguranca" label="Fator de segurança" suffix="%" isPercent step="1" min="10" max="90" help="10% a 90%" />
                  <ParamRow k="reserva_meses" label="Reserva (nº de meses de custo)" suffix="meses" step="1" min="0" help={`Custo op. médio: ${formatCurrency(calc.custo_op_medio)}/mês`} />
                  <ParamRow k="reserva_valor_fixo" label="Reserva (valor fixo, opcional)" step="0.01" min="0" help="Se preenchido, substitui o cálculo por meses" />
                  <ParamRow k="prazo_recompor_meses" label="Prazo p/ recompor reserva" suffix="meses" step="1" min="3" max="36" help="3 a 36 meses" />
                  <ParamRow k="prazo_estresse_meses" label="Prazo do teste de estresse" suffix="meses" step="1" min="1" max="60" help="Projeção conservadora (receita −10%)" />
                  <div className="flex items-center gap-2 mt-3">
                    <Button onClick={salvarParams} disabled={saving} className="bg-blue-600 hover:bg-blue-700 h-9 text-sm" data-testid="cap-salvar-params">
                      {saving ? 'Salvando...' : 'Salvar parâmetros'}
                    </Button>
                    <button onClick={voltarTudoPadrao} disabled={saving} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1" data-testid="cap-reset-tudo">
                      <RotateCcw className="w-3.5 h-3.5" /> Voltar tudo ao padrão
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Simulador de parcela */}
            <div className="rounded-lg border border-zinc-700/50 bg-zinc-800/30 p-4" data-testid="cap-simulador">
              <p className="text-sm text-white flex items-center gap-2 mb-3">
                <Calculator className="w-4 h-4 text-emerald-400" /> Simular uma parcela
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[11px] text-zinc-500">Valor (R$)</label>
                  <input type="number" step="0.01" value={sim.valor} onChange={(e) => setSim({ ...sim, valor: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-white font-numbers" data-testid="cap-sim-valor" />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-500">Prazo (meses)</label>
                  <input type="number" min="1" value={sim.prazo} onChange={(e) => setSim({ ...sim, prazo: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-white font-numbers" data-testid="cap-sim-prazo" />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-500">Juros (%/mês)</label>
                  <input type="number" step="0.01" min="0" value={sim.taxa} onChange={(e) => setSim({ ...sim, taxa: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-white font-numbers" data-testid="cap-sim-taxa" />
                </div>
              </div>
              {pmt > 0 && (
                <div className="mt-3 space-y-2" data-testid="cap-sim-resultado">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 bg-zinc-900 rounded text-center">
                      <p className="text-[10px] text-zinc-500">Parcela</p>
                      <p className="text-sm font-bold text-white font-numbers">{formatCurrency(pmt)}</p>
                    </div>
                    <div className="p-2 bg-zinc-900 rounded text-center">
                      <p className="text-[10px] text-zinc-500">Total</p>
                      <p className="text-sm font-bold text-white font-numbers">{formatCurrency(totalSim)}</p>
                    </div>
                    <div className="p-2 bg-zinc-900 rounded text-center">
                      <p className="text-[10px] text-zinc-500">Juros</p>
                      <p className="text-sm font-bold text-red-400 font-numbers">{formatCurrency(jurosSim)}</p>
                    </div>
                  </div>
                  <div className={`flex items-start gap-2 p-3 rounded-lg border ${veredito === 'cabe' ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
                    {veredito === 'cabe' ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-400 mt-0.5" />}
                    <p className={`text-xs ${veredito === 'cabe' ? 'text-emerald-300' : 'text-red-300'}`}>
                      {veredito === 'cabe'
                        ? `Cabe: a parcela de ${formatCurrency(pmt)} está dentro da sua capacidade de ${formatCurrency(capMensal)}/mês. Sobra ${formatCurrency(margem)}.`
                        : `Não cabe: a parcela de ${formatCurrency(pmt)} passa da sua capacidade de ${formatCurrency(capMensal)}/mês em ${formatCurrency(-margem)}.`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CapacidadeInvestimentoModal;
